import { useState, useEffect, useContext } from 'react';
import './MaintenanceBannerDetails.css';
import { getMaintenanceBannerDetails, putMaintenanceBannerDetails } from '../apiCalls';
import { TextArea, Input, Switch } from '../Input/Input';
import MaintenanceBanner from '../MaintenanceBanner';
import { StoreContext } from '../Store';
import isoDateConverter from '../isoDateConverter';

const MAX_BANNER_TEXT_LENGTH = 200;
const MAX_MORE_INFO_LENGTH = 800;

const MaintenanceBannerDetails = () => {
  const store = useContext(StoreContext);

  const [showPreview, setShowPreview] = useState(false);
  const [disableSave, setDisableSave] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);
  const [errors, setErrors] = useState({});

  const [banner, setBanner] = useState({
    enabled: false,
    bannerText: '',
    moreInfo: '',
    startDate: null,
    endDate: null,
    visibility: '',
    lastModifiedUser: '',
  });

  // handle regular text field changes
  const handleChange = (e) => {
    setBanner((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // handle date field changes
  const handleDateChange = (e) => {
    const newDate = new Date(`${e.target.value} ${banner[e.target.name].toTimeString()}`);
    setBanner((prev) => ({ ...prev, [e.target.name]: newDate }));
  };

  // handle time field changes
  const handleTimeChange = (e) => {
    const newDate = new Date(`${banner[e.target.name].toISOString().split('T')[0]} ${e.target.value}`);
    setBanner((prev) => ({ ...prev, [e.target.name]: newDate }));
  };

  // handle client checkboxes changes
  const handleClientChange = (type, checked) => {
    const internalChecked = type === 'internal' ? checked : ['all', 'internal'].includes(banner.visibility);
    const externalChecked = type === 'external' ? checked : ['all', 'external'].includes(banner.visibility);

    let visibleToUsers = 'none';
    if (internalChecked && externalChecked) {
      visibleToUsers = 'all';
    } else if (internalChecked) {
      visibleToUsers = 'internal';
    } else if (externalChecked) {
      visibleToUsers = 'external';
    }

    setBanner((prev) => ({ ...prev, visibility: visibleToUsers }));
  };

  // handle user clicking the clear button for a field
  const clearValue = (field) => {
    setBanner((prev) => ({ ...prev, [field]: '' }));
  };

  useEffect(() => {
    const fetch = async () => {
      const [data] = await getMaintenanceBannerDetails();

      if (data) {
        const startDate = new Date(data.startDate);
        const endDate = new Date(data.endDate);
        setBanner({
          ...data,
          startDate,
          endDate,
          enabled: data.enabled === '1',
        });
      }
    };
    fetch();
  }, []);

  useEffect(() => {
    if (hasSaved) {
      // display the latest time and user
      setBanner((prev) => ({
        ...prev,
        lastModifiedUser: sessionStorage.getItem('name'),
        lastModifiedDate: new Date().toISOString(),
      }));

      // if 'Saved successfully' message showing, hide it after 5 secs
      setTimeout(() => setHasSaved(false), 5000);
    }
  }, [hasSaved]);

  // some basic error validation checking
  useEffect(() => {
    const validationErrors = {};
    if (banner.bannerText.length >= MAX_BANNER_TEXT_LENGTH) {
      validationErrors.bannerText = `Banner text must be less than ${MAX_BANNER_TEXT_LENGTH} characters.`;
    }
    if (banner.moreInfo.length >= MAX_MORE_INFO_LENGTH) {
      validationErrors.moreInfo = `More info text must be less than ${MAX_MORE_INFO_LENGTH} characters.`;
    }
    if (banner.startDate > banner.endDate) {
      validationErrors.startDate = 'Start date must occur before the end date.';
    }

    setErrors(validationErrors);
    setDisableSave(!!Object.keys(validationErrors).length);
  }, [banner]);

  const saveBannerDetails = async () => {
    setDisableSave(true);
    try {
      const data = await putMaintenanceBannerDetails(banner);
      if (data[0].message === 'success') {
        setHasSaved(true);

        // refresh the maintenance banner component
        store.maintenanceRefresh[1](true);
      }
      setDisableSave(false);
    } catch (err) {
      console.log(err);
      setDisableSave(false);
    }
  };

  return (
    <>
      <MaintenanceBanner previewData={{ ...banner, enabled: showPreview }} />
      <div className="my-4 semi-fluid-container container-lg">
        <div className="floating-box-newload">
          <div className="maintenance-details-head">
            <h1 className="floating-box-header-newload">Maintenance Banner</h1>
            <button
              type="button"
              className="blue-button-newload"
              aria-label="Preview"
              onClick={() => {
                setShowPreview(!showPreview);
              }}
              style={{ width: '12rem', margin: 0 }}
            >
              {showPreview ? 'Hide' : 'Show'} Preview
            </button>
          </div>

          <div className="maintenance-details-body">
            <div className="maintenance-section">
              <TextArea
                style={{ resize: 'none', height: '76px' }}
                name="bannerText"
                label="Banner Text"
                placeholder="Enter Text"
                disabled={false}
                value={banner.bannerText}
                onChange={handleChange}
                error={errors.bannerText}
              />
              <div className="maintenance-chars">
                <div className="char-count">
                  Char count {banner.bannerText.length} / {MAX_BANNER_TEXT_LENGTH}
                </div>
                <button className="clear-text-button" onClick={() => clearValue('bannerText')}>
                  Clear
                </button>
              </div>
            </div>

            <div className="maintenance-section">
              <TextArea
                style={{ resize: 'none', height: '152px' }}
                name="moreInfo"
                label="More Info"
                placeholder="Enter Text"
                disabled={false}
                value={banner.moreInfo}
                onChange={handleChange}
                info="When entered, this automatically adds link to the banner text, displayed in a modal"
                error={errors.moreInfo}
              />
              <div className="maintenance-chars">
                <div className="char-count">
                  Char count {banner.moreInfo.length} / {MAX_MORE_INFO_LENGTH}
                </div>
                <button className="clear-text-button" onClick={() => clearValue('moreInfo')}>
                  Clear
                </button>
              </div>
            </div>

            <div className="maintenance-section maintenance-details-dates">
              <div className="maintenance-details-date">
                <Input
                  label="Start Date &amp; Time"
                  name="startDate"
                  type="date"
                  required
                  value={banner?.startDate?.toISOString().split('T')[0]}
                  onChange={handleDateChange}
                  error={errors.startDate}
                />
                <Input
                  label=""
                  name="startDate"
                  type="time"
                  required
                  value={banner?.startDate?.toTimeString().slice(0, 5)}
                  onChange={handleTimeChange}
                />
              </div>
              <div className="maintenance-details-date">
                <Input
                  label="End Date &amp; Time"
                  name="endDate"
                  type="date"
                  required
                  value={banner?.endDate?.toISOString().split('T')[0]}
                  onChange={handleDateChange}
                />
                <Input
                  label=""
                  name="endDate"
                  type="time"
                  required
                  value={banner?.endDate?.toTimeString().slice(0, 5)}
                  onChange={handleTimeChange}
                />
              </div>
            </div>

            <div className="maintenance-section maintenance-details-clients">
              <div className="maintenance-subheading">Which clients is the banner being displayed to?</div>
              <div className="client-checkboxes">
                <div className="client-checkbox">
                  <input
                    type="checkbox"
                    name="internal"
                    id="internal"
                    checked={['all', 'internal'].includes(banner.visibility)}
                    onChange={(e) => handleClientChange('internal', e.target.checked)}
                  />
                  <label htmlFor="internal">Internal</label>
                </div>
                <div className="client-checkbox">
                  <input
                    type="checkbox"
                    name="external"
                    id="external"
                    checked={['all', 'external'].includes(banner.visibility)}
                    onChange={(e) => handleClientChange('external', e.target.checked)}
                  />
                  <label htmlFor="external">External</label>
                </div>
              </div>
            </div>

            <div className="maintenance-details-enabled">
              <div className="maintenance-subheading">Enabled / Disabled</div>
              <Switch name="enabled" checked={banner.enabled} onChange={handleChange} value="1" />
            </div>
          </div>

          <div className="maintenance-details-footer">
            <span>
              Last modified by: {banner?.lastModifiedUser} on {isoDateConverter(banner?.lastModifiedDate)}
            </span>
            <div>
              {hasSaved && <span className="text-success bold me-3">Successfully saved.</span>}
              <button className="stepper-button" onClick={saveBannerDetails} disabled={disableSave}>
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MaintenanceBannerDetails;
